import { useState, useEffect, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  fetchAllInventoryCars,
  fetchVehicleDetailsById,
  type InventoryCarItem,
  type VehicleDetailsItem,
} from "@/data/carsData";
import {
  ChevronLeft,
  Gauge,
  // Calendar,
  Settings2,
  Users,
  Fuel,
  Car,
  Palette,
  Zap,
  Shield,
  ShieldCheck,
  CheckCircle,
  Phone,
  Mail,
  Star,
  Calculator,
  Wrench,
  ChevronDown,
  ChevronUp,
  Share2,
  // Heart,
  DoorOpen,
  Sun,
  Volume2,
  Monitor,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import "./VehicleDetails.css";

// Scroll reveal hook
function useScrollReveal() {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("revealed");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -50px 0px" },
    );

    const observeElements = () => {
      document.querySelectorAll(".scroll-reveal").forEach((el) => {
        if (!el.classList.contains("revealed")) {
          observer.observe(el);
        }
      });
    };

    observeElements();

    const mutationObserver = new MutationObserver(() => {
      observeElements();
    });

    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => {
      observer.disconnect();
      mutationObserver.disconnect();
    };
  }, []);
}

// Accordion component for features
function FeatureAccordion({
  title,
  icon: Icon,
  children,
  isOpen,
  onToggle,
}: {
  title: string;
  icon: React.ElementType;
  children: React.ReactNode;
  isOpen: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-5 text-left hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
            <Icon className="w-5 h-5 text-red-600" />
          </div>
          <span className="font-semibold text-gray-900 text-lg">{title}</span>
        </div>
        <div
          className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${isOpen ? "bg-red-600 text-white" : "bg-gray-100 text-gray-600"}`}
        >
          {isOpen ? (
            <ChevronUp className="w-5 h-5" />
          ) : (
            <ChevronDown className="w-5 h-5" />
          )}
        </div>
      </button>
      <div
        className={`overflow-hidden transition-all duration-300 ${isOpen ? "max-h-[2000px]" : "max-h-0"}`}
      >
        <div className="p-5 border-t border-gray-100">{children}</div>
      </div>
    </div>
  );
}

// Spec card component
function SpecCard({
  icon: Icon,
  label,
  value,
  delay,
}: {
  icon: React.ElementType;
  label: string;
  value: string;
  delay: number;
}) {
  return (
    <div
      className="scroll-reveal bg-white rounded-xl p-5 border border-gray-200 hover:shadow-lg hover:border-red-200 transition-all group"
      style={{ transitionDelay: `${delay}ms` }}
    >
      <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-red-100 transition-colors">
        <Icon className="w-6 h-6 text-gray-600 group-hover:text-red-600 transition-colors" />
      </div>
      <p className="text-sm text-gray-500 mb-1">{label}</p>
      <p className="font-semibold text-gray-900">{value}</p>
    </div>
  );
}

const getFeatureSectionKey = (title: string) =>
  title
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

function getFeatureAccordionIcon(title: string): React.ElementType {
  switch (title.trim().toLowerCase()) {
    case "mechanical":
      return Wrench;
    case "exterior":
      return Sun;
    case "entertainment":
      return Volume2;
    case "interior":
      return Monitor;
    case "safety":
      return Shield;
    case "comfort":
      return Star;
    default:
      return Star;
  }
}

const getInstallmentHeading = (type?: string) => {
  const cleaned = type?.trim() ?? "";
  if (!cleaned) return "";
  return `Est. ${cleaned.replace(/^by\s+/i, "")}`;
};

const getInstallmentMeta = (percentage?: string, duration?: number | null) => {
  const pct = percentage?.trim() ?? "";
  const dur = duration ?? null;

  if (pct && dur !== null) return `${pct}% for ${dur} months`;
  if (pct) return `${pct}%`;
  if (dur !== null) return `${dur}`;
  return "";
};

export default function VehicleDetails() {
  useScrollReveal();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [openAccordion, setOpenAccordion] = useState<string | null>(null);
  // const [isLiked, setIsLiked] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [showDesktopNameRibbon, setShowDesktopNameRibbon] = useState(false);

  const heroSectionRef = useRef<HTMLElement | null>(null);
  const ribbonEndRef = useRef<HTMLDivElement | null>(null);

  const [vehicle, setVehicle] = useState<VehicleDetailsItem | null>(null);
  const [similarVehicles, setSimilarVehicles] = useState<InventoryCarItem[]>(
    [],
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const galleryImages = useMemo(() => {
    if (!vehicle) return [];
    if (vehicle.images?.length) return vehicle.images;
    return vehicle.image ? [vehicle.image] : [];
  }, [vehicle]);

  const featureSections = useMemo(() => {
    const sectionMap = new Map<
      string,
      { key: string; title: string; items: string[] }
    >();

    (vehicle?.features ?? []).forEach((feature) => {
      const title = feature.type.trim() || "Other Features";
      const key = getFeatureSectionKey(title);
      const name = feature.name.trim();

      if (!name) return;

      if (!sectionMap.has(key)) {
        sectionMap.set(key, {
          key,
          title,
          items: [],
        });
      }

      const section = sectionMap.get(key)!;

      if (!section.items.includes(name)) {
        section.items.push(name);
      }
    });

    return Array.from(sectionMap.values());
  }, [vehicle]);

  useEffect(() => {
    if (featureSections.length === 0) {
      setOpenAccordion(null);
      return;
    }

    setOpenAccordion((prev) =>
      prev && featureSections.some((section) => section.key === prev)
        ? prev
        : featureSections[0].key,
    );
  }, [featureSections]);

  useEffect(() => {
    let isMounted = true;

    const loadVehicle = async () => {
      const vehicleId = (id ?? "").trim();

      if (!vehicleId) {
        if (isMounted) {
          setVehicle(null);
          setSimilarVehicles([]);
          setLoading(false);
          setError("");
        }
        return;
      }

      try {
        setLoading(true);
        setError("");
        setSelectedImageIndex(0);

        const [vehicleData, allCars] = await Promise.all([
          fetchVehicleDetailsById(vehicleId),
          fetchAllInventoryCars({ ordBy: 2 }),
        ]);

        if (!isMounted) return;

        setVehicle(vehicleData);

        if (vehicleData) {
          setSimilarVehicles(
            allCars
              .filter(
                (car) =>
                  car.id !== vehicleData.id &&
                  (car.make === vehicleData.make ||
                    car.bodyStyle === vehicleData.bodyStyle),
              )
              .slice(0, 3),
          );
        } else {
          setSimilarVehicles([]);
          setError("Vehicle not found.");
        }
      } catch (err) {
        if (!isMounted) return;

        setVehicle(null);
        setSimilarVehicles([]);
        setError(
          err instanceof Error ? err.message : "Unable to load vehicle.",
        );
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadVehicle();

    return () => {
      isMounted = false;
    };
  }, [id]);

  useEffect(() => {
    const desktopMq = window.matchMedia("(min-width: 1280px)");

    const updateFloatingRibbon = () => {
      const heroEl = heroSectionRef.current;
      const endEl = ribbonEndRef.current;

      if (!heroEl || !endEl || !desktopMq.matches) {
        setShowDesktopNameRibbon(false);
        return;
      }

      const heroRect = heroEl.getBoundingClientRect();
      const endRect = endEl.getBoundingClientRect();

      const startOffset = 150;
      const endOffset = 180;

      const hasPassedHeroSection = heroRect.bottom <= startOffset;
      const isBeforeRibbonEnd = endRect.top > endOffset;

      setShowDesktopNameRibbon(hasPassedHeroSection && isBeforeRibbonEnd);
    };

    updateFloatingRibbon();

    window.addEventListener("scroll", updateFloatingRibbon, { passive: true });
    window.addEventListener("resize", updateFloatingRibbon);

    if (typeof desktopMq.addEventListener === "function") {
      desktopMq.addEventListener("change", updateFloatingRibbon);
    } else {
      desktopMq.addListener(updateFloatingRibbon);
    }

    return () => {
      window.removeEventListener("scroll", updateFloatingRibbon);
      window.removeEventListener("resize", updateFloatingRibbon);

      if (typeof desktopMq.removeEventListener === "function") {
        desktopMq.removeEventListener("change", updateFloatingRibbon);
      } else {
        desktopMq.removeListener(updateFloatingRibbon);
      }
    };
  }, [vehicle?.id, similarVehicles.length]);

  if (loading) {
    return (
      <div className="min-h-screen pt-32 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto text-center">
          <div className="w-24 h-24 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-6">
            <Car className="w-12 h-12 text-gray-400" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            Loading Vehicle...
          </h1>
          <p className="text-gray-600">
            Please wait while we fetch the details.
          </p>
        </div>
      </div>
    );
  }
  if (!vehicle) {
    return (
      <div className="min-h-screen pt-32 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto text-center">
          <div className="w-24 h-24 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-6">
            <Car className="w-12 h-12 text-gray-400" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            Vehicle Not Found
          </h1>
          <p className="text-gray-600 mb-8">
            {error || `Sorry, we couldn't find a vehicle with ID: ${id}`}
          </p>
          <button
            onClick={() => navigate("/car-inventory")}
            className="inline-flex items-center gap-2 bg-red-600 text-white px-6 py-3 rounded-full font-semibold hover:bg-red-700 transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
            Back to Inventory
          </button>
        </div>
      </div>
    );
  }

  const toggleAccordion = (key: string) => {
    setOpenAccordion(openAccordion === key ? null : key);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Breadcrumb & Actions Bar */}
      <div className="sticky top-20 z-40 bg-gray-50 pt-4 sm:pt-5">
        <div className="bg-white border-b border-gray-200 shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <button
                onClick={() => navigate("/car-inventory")}
                className="flex items-center gap-2 text-gray-600 hover:text-red-600 transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
                <span className="font-medium">Back to Inventory</span>
              </button>

              <div className="flex items-center gap-3">
                {/* <button
                  onClick={() => setIsLiked(!isLiked)}
                  className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${isLiked ? "bg-red-100 text-red-600" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
                >
                  <Heart
                    className={`w-5 h-5 ${isLiked ? "fill-current" : ""}`}
                  />
                </button> */}
                <button className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center text-gray-600 hover:bg-gray-200 transition-colors">
                  <Share2 className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        className={`vehicle-name-ribbon-shell ${
          showDesktopNameRibbon ? "is-visible" : ""
        }`}
        aria-hidden={!showDesktopNameRibbon}
      >
        <div className="vehicle-name-ribbon">
          <span className="vehicle-name-ribbon__text">
            {vehicle.year} {vehicle.make} {vehicle.model}
          </span>
        </div>
      </div>

      {/* Hero Section */}
      <section
        ref={heroSectionRef}
        className="vehicle-hero-section pt-5 sm:pt-7 lg:pt-8 pb-10 sm:pb-12"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="vehicle-hero-grid grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 items-start">
            {/* Left - Image Gallery */}
            <div className="scroll-reveal min-w-0">
              <div className="vehicle-gallery-main relative rounded-[24px] sm:rounded-3xl overflow-hidden shadow-2xl bg-gray-900">
                <img
                  src={galleryImages[selectedImageIndex] || galleryImages[0]}
                  alt={`${vehicle.year} ${vehicle.make} ${vehicle.model} - image ${selectedImageIndex + 1}`}
                  className="vehicle-gallery-image w-full h-[250px] sm:h-[340px] md:h-[420px] lg:h-[500px] object-cover"
                />

                {/* Overlays */}
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900/60 via-transparent to-transparent" />

                {/* Badges */}
                <div className="vehicle-hero-badges absolute top-3 left-3 sm:top-4 sm:left-4 flex flex-wrap gap-2 max-w-[calc(100%-24px)] sm:max-w-none">
                  {vehicle.isGoodPrice && (
                    <Badge className="bg-green-500 text-white px-3 py-1 text-[11px] sm:text-xs">
                      <Star className="w-3 h-3 mr-1 fill-current" />
                      {vehicle.tagLabel || "GOOD PRICE"}
                    </Badge>
                  )}
                  <Badge className="bg-red-600 text-white px-3 py-1 text-[11px] sm:text-xs">
                    {vehicle.condition}
                  </Badge>
                </div>

                {/* Watch Video Button */}
                {/* <button className="vehicle-watch-video absolute bottom-3 sm:bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2 bg-white/90 backdrop-blur-sm text-gray-900 px-4 sm:px-5 py-2 sm:py-2.5 rounded-full font-medium text-sm sm:text-base hover:bg-white transition-colors whitespace-nowrap">
                  <Play className="w-4 h-4 fill-current" />
                  Watch Video
                </button> */}
              </div>

              {/* Thumbnail Strip */}
              {galleryImages.length > 1 && (
                <div className="thumbnail-scroll flex gap-2.5 sm:gap-3 mt-3 sm:mt-4 overflow-x-auto pb-2">
                  {galleryImages.map((img, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => setSelectedImageIndex(index)}
                      className={`vehicle-thumb flex-shrink-0 w-20 h-14 sm:w-24 sm:h-16 rounded-lg overflow-hidden cursor-pointer border-2 transition-all ${
                        selectedImageIndex === index
                          ? "border-red-600 ring-2 ring-red-200"
                          : "border-transparent hover:border-gray-300"
                      }`}
                    >
                      <img
                        src={img}
                        alt={`${vehicle.year} ${vehicle.make} ${vehicle.model} thumbnail ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Right - Vehicle Info */}
            <div
              className="vehicle-info-panel scroll-reveal min-w-0"
              style={{ transitionDelay: "100ms" }}
            >
              {/* Title Section */}
              <div className="mb-5 sm:mb-6">
                <div className="vehicle-stock-vin flex flex-wrap items-center gap-2 sm:gap-3 mb-3">
                  <Badge
                    variant="outline"
                    className="text-gray-600 text-[11px] sm:text-xs max-w-full"
                  >
                    <Car className="w-3 h-3 mr-1 shrink-0" />
                    Stock: {vehicle.stockNumber}
                  </Badge>

                  <Badge
                    variant="outline"
                    className="vehicle-vin-badge text-gray-600 text-[11px] sm:text-xs max-w-full"
                  >
                    <span className="break-all">VIN: {vehicle.vin}</span>
                  </Badge>
                </div>

                <h1 className="vehicle-hero-title text-[1.95rem] sm:text-4xl lg:text-5xl font-bold leading-[1.12] sm:leading-[1.18] text-gray-900 mb-2 pt-4">
                  {vehicle.year} {vehicle.make} {vehicle.model}
                </h1>
                <p className="vehicle-hero-trim text-lg sm:text-xl text-gray-600">
                  {vehicle.trim}
                </p>
              </div>

              {/* Pricing Section */}
              <div className="vehicle-pricing-card bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-4 sm:p-6 mb-5 sm:mb-6">
                <div className="vehicle-pricing-top flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between mb-4">
                  <div>
                    <p className="text-gray-400 text-xs sm:text-sm mb-1">
                      Dealer Price
                    </p>
                    <p className="text-3xl sm:text-4xl font-bold text-white leading-none">
                      {vehicle.price !== null
                        ? `$${vehicle.price.toLocaleString()}`
                        : ""}
                    </p>
                    <p className="text-gray-400 text-xs sm:text-sm mt-1">
                      + taxes and fees
                    </p>
                  </div>

                  <div className="vehicle-pricing-side text-left sm:text-right">
                    <p className="text-gray-400 text-xs sm:text-sm mb-1 min-h-[20px]">
                      {getInstallmentHeading(vehicle.installmentType)}
                    </p>

                    <p className="text-2xl sm:text-3xl font-bold text-green-400 leading-none min-h-[36px]">
                      {vehicle.installmentAmount !== null
                        ? `$${vehicle.installmentAmount.toLocaleString()}`
                        : ""}
                    </p>

                    <p className="text-gray-400 text-xs sm:text-sm mt-1 min-h-[20px]">
                      {getInstallmentMeta(
                        vehicle.installmentPercentage,
                        vehicle.installmentDuration,
                      )}
                    </p>
                  </div>
                </div>

                {/* <button className="w-full sm:w-auto text-left text-gray-400 text-sm hover:text-white transition-colors inline-flex items-center gap-1">
                  Estimate Your Payments
                  <ArrowRight className="w-4 h-4" />
                </button> */}
              </div>

              {/* Action Buttons */}
              <div className="space-y-3">
                <button
                  onClick={() => navigate("/finance#auto-loan-calculator")}
                  className="vehicle-primary-action w-full flex items-center justify-center gap-2 bg-red-600 text-white px-5 sm:px-6 py-3.5 sm:py-4 rounded-xl font-bold text-base sm:text-lg hover:bg-red-700 transition-all hover:shadow-lg hover:shadow-red-500/30"
                >
                  <Calculator className="w-5 h-5 shrink-0" />
                  Auto Loan Calculator
                </button>

                {vehicle.carfxUrl && (
                  <a
                    href={vehicle.carfxUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="vehicle-history-badge-wrap vehicle-history-badge-wrap--link"
                    aria-label="Open vehicle history report"
                  >
                    <img
                      src="/images/car-fax-logo.jpeg"
                      alt="Vehicle History"
                      className="vehicle-history-badge-image"
                      loading="lazy"
                      decoding="async"
                    />
                  </a>
                )}
              </div>

              {/* Quick Contact */}
              <div className="vehicle-contact-row mt-5 sm:mt-6 flex flex-wrap items-center justify-start sm:justify-center gap-4 sm:gap-6 text-sm text-gray-600">
                <a
                  href="tel:6043588081"
                  className="flex items-center gap-2 hover:text-red-600 transition-colors"
                >
                  <Phone className="w-4 h-4 shrink-0" />
                  (604) 358-8081
                </a>
                <a
                  href="mailto:info@bigboysmotorclub.com"
                  className="flex items-center gap-2 hover:text-red-600 transition-colors"
                >
                  <Mail className="w-4 h-4 shrink-0" />
                  Email Us
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Vehicle Specs Grid */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 scroll-reveal">
            <Badge className="mb-4 bg-red-100 text-red-700 border-0">
              <Gauge className="w-4 h-4 mr-2" />
              Specifications
            </Badge>
            <h2 className="text-3xl font-bold text-gray-900">
              Everything You Need to <span className="text-red-600">Know</span>
            </h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            <SpecCard
              icon={Gauge}
              label="Kilometres"
              value={vehicle.km !== null ? vehicle.km.toLocaleString() : ""}
              delay={0}
            />
            <SpecCard
              icon={CheckCircle}
              label="Condition"
              value={vehicle.condition}
              delay={50}
            />
            <SpecCard
              icon={Car}
              label="Body Style"
              value={vehicle.bodyStyle}
              delay={100}
            />
            <SpecCard
              icon={Zap}
              label="Engine"
              value={vehicle.engine}
              delay={150}
            />
            <SpecCard
              icon={Palette}
              label="Exterior Colour"
              value={vehicle.exteriorColor}
              delay={200}
            />
            <SpecCard
              icon={Settings2}
              label="Transmission"
              value={vehicle.transmission}
              delay={250}
            />
            <SpecCard
              icon={Shield}
              label="Drive Train"
              value={vehicle.drivetrain}
              delay={300}
            />
            <SpecCard
              icon={Users}
              label="Passengers"
              value={vehicle.passengers || ""}
              delay={350}
            />
            <SpecCard
              icon={DoorOpen}
              label="Doors"
              value={vehicle.doors || ""}
              delay={400}
            />
            <SpecCard
              icon={Fuel}
              label="Fuel Type"
              value={vehicle.fuelType}
              delay={450}
            />
            <SpecCard
              icon={Gauge}
              label="City Economy"
              value={vehicle.mileage || ""}
              delay={500}
            />
            <SpecCard
              icon={ShieldCheck}
              label="Insurance"
              value={vehicle.insurance || ""}
              delay={550}
            />
          </div>

          {/* Description */}
          <div className="mt-8 scroll-reveal">
            <div className="bg-gray-50 rounded-xl p-6 border-l-4 border-red-600">
              <p className="text-gray-700 leading-relaxed">
                {vehicle.description || ""}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features & Equipment Section */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 scroll-reveal">
            <Badge className="mb-4 bg-blue-100 text-blue-700 border-0">
              <Wrench className="w-4 h-4 mr-2" />
              Standard Equipment
            </Badge>
            <h2 className="text-3xl font-bold text-gray-900">
              Features & <span className="text-red-600">Equipment</span>
            </h2>
          </div>

          <div className="space-y-4 scroll-reveal">
            {featureSections.length > 0 ? (
              featureSections.map((section) => {
                const Icon = getFeatureAccordionIcon(section.title);

                return (
                  <FeatureAccordion
                    key={section.key}
                    title={section.title}
                    icon={Icon}
                    isOpen={openAccordion === section.key}
                    onToggle={() => toggleAccordion(section.key)}
                  >
                    <div className="grid md:grid-cols-2 gap-3">
                      {section.items.map((feature, index) => (
                        <div
                          key={`${section.key}-${index}`}
                          className="flex items-center gap-3 text-gray-700"
                        >
                          <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                          <span>{feature}</span>
                        </div>
                      ))}
                    </div>
                  </FeatureAccordion>
                );
              })
            ) : (
              <div className="bg-white border border-gray-200 rounded-xl p-6 text-center text-gray-500">
                No features available for this vehicle.
              </div>
            )}
          </div>

          {/* Disclaimer */}
          <div className="mt-6 p-4 bg-yellow-50 rounded-xl border border-yellow-200 scroll-reveal">
            <p className="text-sm text-yellow-800">
              <strong>Disclaimer:</strong> Standard Equipment is the default
              equipment supplied for the Make and Model of this vehicle, but may
              not represent the final vehicle with additional / altered
              equipment options. Please contact the dealership for verification
              of any information listed herein.
            </p>
          </div>
        </div>
      </section>

      {/* Similar Vehicles Section */}
      {similarVehicles.length > 0 && (
        <section className="py-12 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10 scroll-reveal">
              <Badge className="mb-4 bg-green-100 text-green-700 border-0">
                <Car className="w-4 h-4 mr-2" />
                You May Also Like
              </Badge>
              <h2 className="text-3xl font-bold text-gray-900">
                Similar <span className="text-red-600">Vehicles</span>
              </h2>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {similarVehicles.map((car, index) => (
                <div
                  key={car.id}
                  className="scroll-reveal bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-lg transition-all cursor-pointer group"
                  style={{ transitionDelay: `${index * 100}ms` }}
                  onClick={() => navigate(`/vehicle-details/${car.id}`)}
                >
                  <div className="relative h-48 overflow-hidden">
                    {car.image ? (
                      <img
                        src={car.image}
                        alt={`${car.year} ${car.make} ${car.model}`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : (
                      <div className="w-full h-full bg-gray-100" />
                    )}
                    {car.isGoodPrice && (
                      <Badge className="absolute top-3 left-3 bg-green-500 text-white">
                        <Star className="w-3 h-3 mr-1 fill-current" />
                        GOOD PRICE
                      </Badge>
                    )}
                  </div>
                  <div className="p-5">
                    <h3 className="font-bold text-gray-900 mb-1">
                      {car.year} {car.make} {car.model}
                    </h3>
                    <p className="text-gray-500 text-sm mb-3">{car.trim}</p>
                    <div className="flex items-center justify-between">
                      <p className="text-xl font-bold text-red-600">
                        {car.price !== null
                          ? `$${car.price.toLocaleString()}`
                          : ""}
                      </p>
                      <p className="text-sm text-gray-500">
                        {car.km !== null ? `${car.km.toLocaleString()} km` : ""}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <div
        ref={ribbonEndRef}
        className="vehicle-name-ribbon-end"
        aria-hidden="true"
      />

      {/* CTA Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="scroll-reveal relative bg-gradient-to-br from-red-600 via-red-700 to-red-800 rounded-3xl overflow-hidden">
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-20">
              <div className="absolute top-0 right-0 w-96 h-96 bg-white rounded-full filter blur-3xl" />
              <div className="absolute bottom-0 left-0 w-96 h-96 bg-yellow-400 rounded-full filter blur-3xl" />
            </div>

            <div className="relative z-10 px-8 py-12 md:px-16 md:py-16 text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Ready to Take This {vehicle.make} for a Spin?
              </h2>
              <p className="text-xl text-white/90 max-w-2xl mx-auto mb-8">
                Schedule a test drive today and experience this {vehicle.year}{" "}
                {vehicle.make} {vehicle.model} firsthand. Our team is ready to
                assist you!
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="tel:6043588081"
                  className="inline-flex items-center justify-center gap-2 bg-white text-red-600 px-8 py-4 rounded-full font-bold text-lg hover:shadow-xl transition-all"
                >
                  <Phone className="w-5 h-5" />
                  Call (604) 358-8081
                </a>
                {/* <button className="inline-flex items-center justify-center gap-2 bg-white/20 backdrop-blur-sm text-white border-2 border-white/50 px-8 py-4 rounded-full font-bold text-lg hover:bg-white/30 transition-all">
                  <Calendar className="w-5 h-5" />
                  Schedule Test Drive
                </button> */}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bottom Disclaimer */}
      <section className="pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="scroll-reveal p-6 bg-gray-100 rounded-xl">
            <p className="text-sm text-gray-600 leading-relaxed">
              Please contact the dealership for verification of any information
              listed herein. Due to human error and other possible
              complications, we cannot guarantee the accuracy of the information
              included herein, despite all reasonable attempts being made to
              ensure its accuracy. The information may differ in specification,
              price, or any other listed characteristic herein.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
